import { ApiError, request } from '../api/client'

/**
 * La facturation de la plateforme (migration 0079), vue du panneau. Ce module n'est importé
 * que par les écrans du portail d'administration : le bundle des exploitants ne le porte pas.
 *
 * LES MONTANTS ARRIVENT DE LA BASE, ARRONDIS AU MILLIME, et ne sont jamais recalculés ici :
 * l'écran affiche ce que la facture dit, pas ce qu'il croit qu'elle devrait dire.
 */

export type InvoiceStatus = 'unpaid' | 'partial' | 'paid' | 'overdue' | 'cancelled' | 'credit_note'

export type PaymentMethod = 'transfer' | 'cheque' | 'cash' | 'card' | 'other'

export const PAYMENT_METHODS: readonly PaymentMethod[] = ['transfer', 'cheque', 'cash', 'card', 'other']

export interface SellerProfile {
  legalName: string
  taxId: string | null
  address: string | null
  city: string | null
  postalCode: string | null
  phone: string | null
  email: string | null
  bankName: string | null
  bankAccount: string | null
  vatRate: number
  stampDuty: number
  paymentTermsDays: number
  autoInvoice: boolean
  footer: string | null
  isComplete: boolean
}

export type SellerProfileInput = Omit<SellerProfile, 'isComplete'>

export interface InvoiceSummary {
  id: number
  number: string
  kind: 'invoice' | 'credit_note'
  origin: 'subscription' | 'manual' | 'credit'
  organizationId: number
  clientName: string
  issuedOn: string
  dueOn: string
  periodStart: string | null
  periodEnd: string | null
  totalTtc: number
  balance: number
  status: InvoiceStatus
  creditedNumber: string | null
}

export interface InvoiceParty {
  legalName: string
  taxId: string | null
  address: string | null
  city: string | null
  postalCode: string | null
  governorate: string | null
  phone: string | null
  email: string | null
}

export interface InvoiceDocument {
  id: number
  number: string
  kind: 'invoice' | 'credit_note'
  origin: string
  organizationId: number
  issuedOn: string
  dueOn: string
  periodStart: string | null
  periodEnd: string | null
  currencyCode: string
  vatRate: number
  totalHt: number
  totalVat: number
  stampDuty: number
  totalTtc: number
  seller: InvoiceParty
  buyer: InvoiceParty
  note: string | null
  reason: string | null
  balance: number
  status: InvoiceStatus
  creditedInvoice: { id: number; number: string; issuedOn: string } | null
  lines: { position: number; description: string; quantity: number; unitPrice: number; amountHt: number }[]
  payments: {
    id: number
    paidOn: string
    amount: number
    method: PaymentMethod
    reference: string | null
    note: string | null
    voidedAt: string | null
    voidReason: string | null
  }[]
  creditNotes: { id: number; number: string; issuedOn: string; totalTtc: number | null; reason: string | null }[]
}

export interface NewInvoiceLine {
  description: string
  quantity: number
  unitPrice: number
}

export const billingApi = {
  getSeller: (token: string, signal?: AbortSignal) =>
    request<SellerProfile>('/api/v1/platform/billing/seller', { token, signal }),

  setSeller: (token: string, body: SellerProfileInput) =>
    request<SellerProfile>('/api/v1/platform/billing/seller', { method: 'PUT', token, body }),

  listInvoices: (token: string, organizationId?: number, signal?: AbortSignal) =>
    request<InvoiceSummary[]>(
      organizationId === undefined ? '/api/v1/platform/invoices' : `/api/v1/platform/invoices?organizationId=${organizationId}`,
      { token, signal },
    ),

  getInvoice: (token: string, id: number, signal?: AbortSignal) =>
    request<InvoiceDocument>(`/api/v1/platform/invoices/${id}`, { token, signal }),

  createInvoice: (token: string, body: { organizationId: number; issuedOn: string | null; lines: NewInvoiceLine[]; note: string | null }) =>
    request<{ id: number }>('/api/v1/platform/invoices', { method: 'POST', token, body }),

  invoiceCurrentPeriod: (token: string, organizationId: number) =>
    request<{ id: number }>(`/api/v1/platform/clients/${organizationId}/invoices/current-period`, { method: 'POST', token }),

  recordPayment: (
    token: string,
    invoiceId: number,
    body: { paidOn: string | null; amount: number; method: PaymentMethod; reference: string | null; note: string | null },
  ) =>
    request<{ paymentId: number; balance: number; reactivated: boolean }>(`/api/v1/platform/invoices/${invoiceId}/payments`, {
      method: 'POST',
      token,
      body,
    }),

  voidPayment: (token: string, paymentId: number, reason: string) =>
    request<void>(`/api/v1/platform/payments/${paymentId}/void`, { method: 'POST', token, body: { reason } }),

  createCreditNote: (token: string, invoiceId: number, body: { amountHt: number | null; reason: string }) =>
    request<{ id: number }>(`/api/v1/platform/invoices/${invoiceId}/credit-notes`, { method: 'POST', token, body }),

  /**
   * Le PDF, en Blob. Pas par `request`, qui lit du JSON : on lit l'erreur en JSON seulement
   * si la réponse en est une, comme `downloadExport`.
   */
  downloadPdf: async (token: string, id: number): Promise<Blob> => {
    const base: string = import.meta.env.VITE_API_BASE_URL ?? ''
    let response: Response
    try {
      response = await fetch(`${base}/api/v1/platform/invoices/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      })
    } catch (cause) {
      throw new ApiError(0, 'error.network', String(cause))
    }

    if (!response.ok) {
      const text = await response.text()
      let code: string | undefined
      try {
        code = (JSON.parse(text) as { code?: string }).code
      } catch {
        /* réponse qui n'est pas du JSON : pas de code */
      }
      throw new ApiError(response.status, code, text || response.statusText)
    }

    return await response.blob()
  },
}

/** Enregistre un Blob sous un nom de fichier, par un lien temporaire. */
export function enregistrer(blob: Blob, nom: string) {
  const url = URL.createObjectURL(blob)
  const lien = document.createElement('a')
  lien.href = url
  lien.download = nom
  document.body.appendChild(lien)
  lien.click()
  lien.remove()
  URL.revokeObjectURL(url)
}

/** La couleur d'un état : ce qui attend un geste en orange, l'échu en rouge. */
export function toneOf(status: InvoiceStatus): 'info' | 'succes' | 'avertissement' | 'erreur' {
  switch (status) {
    case 'paid':
      return 'succes'
    case 'overdue':
      return 'erreur'
    case 'partial':
    case 'unpaid':
      return 'avertissement'
    default:
      return 'info'
  }
}
